import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import {
    useNavigate,
} from 'react-router-dom'

import {
    Spin,
    Typography,
    message,
} from 'antd'

import fetch from 'auth/FetchInterceptor'

import {
    OfflineGameService,
    OfflineMemoryMatchService,
    OfflineMemorySyncService,
} from 'offline'

import {
    env,
} from 'configs/EnvironmentConfig'

const {
    Title,
    Text,
} = Typography

const API_BASE_URL =
    env.API_ENDPOINT_URL

// ======================================================
// HELPERS
// ======================================================

const getImageUrl = (
    url,
) => {
    if (!url) {
        return null
    }

    if (
        url.startsWith(
            'http://',
        ) ||
        url.startsWith(
            'https://',
        ) ||
        url.startsWith(
            'data:',
        )
    ) {
        return url
    }

    return `${API_BASE_URL}${url}`
}

const getMediaUrl = (
    url,
) => {
    if (!url) {
        return null
    }

    if (
        url.startsWith(
            'http://',
        ) ||
        url.startsWith(
            'https://',
        ) ||
        url.startsWith(
            'data:',
        ) ||
        url.startsWith(
            'blob:',
        )
    ) {
        return url
    }

    return `${API_BASE_URL}${url}`
}

const shuffleArray = (
    items,
) => {
    const result = [
        ...items,
    ]

    for (
        let index =
            result.length - 1;
        index > 0;
        index--
    ) {
        const randomIndex =
            Math.floor(
                Math.random() *
                (
                    index +
                    1
                ),
            )

        const temporary =
            result[index]

        result[index] =
            result[
            randomIndex
            ]

        result[
            randomIndex
        ] =
            temporary
    }

    return result
}

const calculateScore = ({
    durationMs,
    errors,
    completed,
}) => {
    if (!completed) {
        return 0
    }

    const seconds =
        durationMs /
        1000

    const calculated =
        10000 -
        Math.round(
            seconds *
            50,
        ) -
        errors *
        200

    return Math.max(
        0,
        calculated,
    )
}

const formatTime = (
    seconds,
) => {
    const safe =
        Math.max(
            0,
            Number(
                seconds ||
                0,
            ),
        )

    const minutes =
        Math.floor(
            safe /
            60,
        )

    const remaining =
        safe %
        60

    return `${String(
        minutes,
    ).padStart(
        2,
        '0',
    )}:${String(
        remaining,
    ).padStart(
        2,
        '0',
    )}`
}

// ======================================================
// MEMORY GAME
// ======================================================

const MemoryGame = ({
    game,
    leadId = null,
    leadValues = null,
    onGameFinished,
}) => {
    const navigate =
        useNavigate()

    // ======================================================
    // ESTADOS
    // ======================================================

    const [
        loading,
        setLoading,
    ] = useState(
        true,
    )

    const [
        viewport,
        setViewport,
    ] = useState(
        () => ({
            width:
                typeof window !==
                    'undefined'
                    ? window.innerWidth
                    : 1920,

            height:
                typeof window !==
                    'undefined'
                    ? window.innerHeight
                    : 1080,
        }),
    )

    const [
        config,
        setConfig,
    ] = useState(
        null,
    )

    const [
        cards,
        setCards,
    ] = useState(
        [],
    )

    const [
        board,
        setBoard,
    ] = useState(
        [],
    )

    const [
        started,
        setStarted,
    ] = useState(
        false,
    )

    const [
        revealing,
        setRevealing,
    ] = useState(
        false,
    )

    const [
        revealCountdown,
        setRevealCountdown,
    ] = useState(
        null,
    )

    const [
        introPhase,
        setIntroPhase,
    ] = useState(
        null,
    )

    const [
        autoFlipPhase,
        setAutoFlipPhase,
    ] = useState(
        null,
    )

    const [
        finished,
        setFinished,
    ] = useState(
        false,
    )

    const [
        locked,
        setLocked,
    ] = useState(
        false,
    )

    const [
        firstCardId,
        setFirstCardId,
    ] = useState(
        null,
    )

    const [
        secondCardId,
        setSecondCardId,
    ] = useState(
        null,
    )

    const [
        attempts,
        setAttempts,
    ] = useState(
        0,
    )

    const [
        errors,
        setErrors,
    ] = useState(
        0,
    )

    const [
        matchedPairs,
        setMatchedPairs,
    ] = useState(
        0,
    )

    const [
        combo,
        setCombo,
    ] = useState(
        0,
    )

    const [
        comboDisplay,
        setComboDisplay,
    ] = useState(
        null,
    )

    const [
        liveScore,
        setLiveScore,
    ] = useState(
        0,
    )

    const [
        remainingSeconds,
        setRemainingSeconds,
    ] = useState(
        60,
    )

    const [
        savingMatch,
        setSavingMatch,
    ] = useState(
        false,
    )

    // ======================================================
    // REFS
    // ======================================================

    const startedAtRef =
        useRef(
            null,
        )

    const finishedRef =
        useRef(
            false,
        )

    const autoStartRef =
        useRef(
            false,
        )

    const musicRef =
        useRef(
            null,
        )

    // ======================================================
    // SINCRONIZAÇÃO DE PARTIDAS OFFLINE
    // ======================================================

    useEffect(() => {
        const sync =
            async () => {
                if (
                    typeof navigator !==
                    'undefined' &&
                    navigator.onLine ===
                    false
                ) {
                    return
                }

                try {
                    await OfflineMemorySyncService
                        .syncPendingMatches()
                } catch (
                error
                ) {
                    console.warn(
                        '[MEMORY] Não foi possível sincronizar partidas pendentes:',
                        error,
                    )
                }
            }

        sync()

        const handleOnline =
            () => {
                sync()
            }

        window.addEventListener(
            'online',
            handleOnline,
        )

        return () => {
            window.removeEventListener(
                'online',
                handleOnline,
            )
        }
    }, [])

    // ======================================================
    // VIEWPORT / RESIZE
    // ======================================================

    useEffect(() => {
        const handleResize =
            () => {
                setViewport({
                    width:
                        window.innerWidth,

                    height:
                        window.innerHeight,
                })
            }

        handleResize()

        window.addEventListener(
            'resize',
            handleResize,
        )

        window.addEventListener(
            'orientationchange',
            handleResize,
        )

        return () => {
            window.removeEventListener(
                'resize',
                handleResize,
            )

            window.removeEventListener(
                'orientationchange',
                handleResize,
            )
        }
    }, [])

    // ======================================================
    // ÁUDIO
    // ======================================================

    const playEffect =
        useCallback(
            (
                url,
            ) => {
                if (
                    !config
                        ?.soundEnabled ||
                    !url
                ) {
                    return
                }

                try {
                    const audio =
                        new Audio(
                            getMediaUrl(
                                url,
                            ),
                        )

                    audio.volume =
                        Math.max(
                            0,
                            Math.min(
                                1,
                                Number(
                                    config
                                        ?.soundVolume ??
                                    0.8,
                                ),
                            ),
                        )

                    audio
                        .play()
                        .catch(
                            () => { },
                        )
                } catch (
                error
                ) {
                    console.warn(
                        '[MEMORY] Falha ao reproduzir áudio:',
                        error,
                    )
                }
            },
            [
                config,
            ],
        )

    // ======================================================
    // MÚSICA
    // ======================================================

    useEffect(() => {
        if (
            !config
                ?.musicEnabled ||
            !config
                ?.musicUrl
        ) {
            if (
                musicRef.current
            ) {
                musicRef
                    .current
                    .pause()

                musicRef.current =
                    null
            }

            return undefined
        }

        const audio =
            new Audio(
                getMediaUrl(
                    config.musicUrl,
                ),
            )

        audio.loop =
            true

        audio.volume =
            Math.max(
                0,
                Math.min(
                    1,
                    Number(
                        config
                            ?.musicVolume ??
                        0.4,
                    ),
                ),
            )

        musicRef.current =
            audio

        audio
            .play()
            .catch(
                () => { },
            )

        return () => {
            audio.pause()

            audio.currentTime =
                0

            if (
                musicRef.current ===
                audio
            ) {
                musicRef.current =
                    null
            }
        }
    }, [
        config?.musicEnabled,
        config?.musicUrl,
        config?.musicVolume,
    ])

    // ======================================================
    // WAIT
    // ======================================================

    const wait =
        useCallback(
            (
                milliseconds,
            ) =>
                new Promise(
                    (
                        resolve,
                    ) => {
                        window.setTimeout(
                            resolve,
                            milliseconds,
                        )
                    },
                ),
            [],
        )

    // ======================================================
    // PARTICIPANTE
    // ======================================================

    const participantData =
        useMemo(
            () => ({
                name:
                    leadValues
                        ?.nome ||
                    leadValues
                        ?.name ||
                    leadValues
                        ?.nome_completo ||
                    leadValues
                        ?.nomeCompleto ||
                    leadValues
                        ?.fullName ||
                    'Participante',

                email:
                    leadValues
                        ?.email ||
                    leadValues
                        ?.e_mail ||
                    undefined,

                phone:
                    leadValues
                        ?.telefone ||
                    leadValues
                        ?.phone ||
                    leadValues
                        ?.celular ||
                    leadValues
                        ?.whatsapp ||
                    undefined,

                cpf:
                    leadValues
                        ?.cpf ||
                    leadValues
                        ?.document ||
                    undefined,

                company:
                    leadValues
                        ?.empresa ||
                    leadValues
                        ?.company ||
                    leadValues
                        ?.empresa_nome ||
                    undefined,

                role:
                    leadValues
                        ?.cargo ||
                    leadValues
                        ?.role ||
                    leadValues
                        ?.funcao ||
                    undefined,

                lgpdAccepted:
                    Boolean(
                        leadId,
                    ),
            }),
            [
                leadId,
                leadValues,
            ],
        )

    // ======================================================
    // CARREGAR CONFIGURAÇÃO
    // ======================================================

    const loadMemoryConfig =
        useCallback(
            async () => {
                if (!game?.id) {
                    return
                }

                try {
                    setLoading(true)

                    let data = null
                    let loadedOffline = false

                    // ==========================================
                    // 1. VERIFICAR SE O PLAYPAGE JÁ ESTÁ OFFLINE
                    // ==========================================

                    const forceOffline =
                        game?.offlineMode === true ||
                        (
                            typeof navigator !== 'undefined' &&
                            navigator.onLine === false
                        )

                    console.log(
                        '[MEMORY] Modo recebido:',
                        forceOffline
                            ? 'OFFLINE'
                            : 'ONLINE',
                    )

                    // ==========================================
                    // 2. OFFLINE: INDEXEDDB PRIMEIRO
                    // ==========================================

                    if (forceOffline) {
                        try {
                            data =
                                await OfflineGameService
                                    .getMemoryConfig(
                                        game.id,
                                    )

                            if (data) {
                                loadedOffline = true

                                console.log(
                                    '[MEMORY] Configuração carregada diretamente do IndexedDB:',
                                    game.id,
                                )
                            }
                        } catch (offlineError) {
                            console.error(
                                '[MEMORY] Erro ao consultar IndexedDB:',
                                offlineError,
                            )
                        }
                    }

                    // ==========================================
                    // 3. ONLINE: TENTAR API
                    // ==========================================

                    if (!data && !forceOffline) {
                        try {
                            data =
                                await fetch({
                                    url:
                                        `/memory-games/${game.id}`,

                                    method:
                                        'get',

                                    silentNetworkError:
                                        true,
                                })

                            if (data) {
                                console.log(
                                    '[MEMORY] Configuração carregada da API:',
                                    game.id,
                                )

                                // ==================================
                                // ATUALIZAR SNAPSHOT OFFLINE
                                // ==================================

                                try {
                                    await OfflineGameService
                                        .saveMemoryConfig(
                                            game.id,
                                            data,
                                        )

                                    console.log(
                                        '[MEMORY] Snapshot Memory atualizado no IndexedDB:',
                                        game.id,
                                    )
                                } catch (cacheError) {
                                    console.warn(
                                        '[MEMORY] Não foi possível atualizar snapshot offline:',
                                        cacheError,
                                    )
                                }
                            }
                        } catch (apiError) {
                            console.warn(
                                '[MEMORY] API indisponível. Tentando IndexedDB...',
                                apiError,
                            )
                        }
                    }

                    // ==========================================
                    // 4. FALLBACK INDEXEDDB
                    // ==========================================

                    if (!data) {
                        try {
                            data =
                                await OfflineGameService
                                    .getMemoryConfig(
                                        game.id,
                                    )

                            if (data) {
                                loadedOffline = true

                                console.log(
                                    '[MEMORY] Configuração recuperada pelo fallback IndexedDB:',
                                    game.id,
                                )
                            }
                        } catch (offlineError) {
                            console.error(
                                '[MEMORY] Falha no fallback IndexedDB:',
                                offlineError,
                            )
                        }
                    }

                    // ==========================================
                    // 5. VALIDAR SNAPSHOT
                    // ==========================================

                    if (!data) {
                        throw new Error(
                            'MEMORY_OFFLINE_DATA_NOT_FOUND',
                        )
                    }

                    const memoryConfig =
                        data?.config

                    if (!memoryConfig) {
                        throw new Error(
                            'MEMORY_CONFIG_NOT_FOUND',
                        )
                    }

                    // ==========================================
                    // IMPORTANTE:
                    // Em snapshot antigo data.game pode existir.
                    // Caso contrário usamos o game recebido.
                    // ==========================================

                    const gameVisual =
                        data?.game ||
                        game ||
                        {}

                    // ==========================================
                    // 6. CARTAS
                    // ==========================================

                    const rawCards =
                        Array.isArray(
                            data?.cards,
                        )
                            ? data.cards
                            : Array.isArray(
                                memoryConfig?.cards,
                            )
                                ? memoryConfig.cards
                                : []

                    const activeCards =
                        rawCards.filter(
                            (item) =>
                                item?.isActive !== false,
                        )

                    if (!activeCards.length) {
                        console.warn(
                            '[MEMORY] Snapshot carregado, mas nenhuma carta ativa foi encontrada.',
                        )
                    }

                    // ==========================================
                    // 7. CONFIGURAÇÃO + VISUAL
                    // ==========================================

                    setConfig({
                        ...memoryConfig,

                        gameLogoWidth:
                            gameVisual?.gameLogoWidth ??
                            110,

                        gameLogoHeight:
                            gameVisual?.gameLogoHeight ??
                            80,

                        gameplayBackgroundUrl:
                            gameVisual?.gameplayBackgroundUrl ||
                            '',

                        gameplayBackgroundColor:
                            gameVisual?.gameplayBackgroundColor ||
                            gameVisual?.backgroundColor ||
                            '#ffffff',

                        gameplayLogoUrl:
                            gameVisual?.gameplayLogoUrl ||
                            '',

                        gameplayPrimaryColor:
                            gameVisual?.gameplayPrimaryColor ||
                            gameVisual?.primaryColor ||
                            '#1677ff',

                        gameplayTextColor:
                            gameVisual?.gameplayTextColor ||
                            gameVisual?.textColor ||
                            '#111827',

                        backgroundFit:
                            gameVisual?.backgroundFit ||
                            'cover',

                        hudBackground:
                            gameVisual?.hudBackground ||
                            'rgba(255,255,255,.94)',

                        hudTextColor:
                            gameVisual?.hudTextColor ||
                            '#0d3f9f',

                        hudLabelColor:
                            gameVisual?.hudLabelColor ||
                            '#0d3f9f',

                        hudBorderRadius:
                            gameVisual?.hudBorderRadius ??
                            999,

                        hudShadow:
                            gameVisual?.hudShadow ??
                            true,

                        cardImageFit:
                            gameVisual?.cardImageFit ||
                            'fill',
                    })

                    setCards(
                        activeCards,
                    )

                    setRemainingSeconds(
                        Number(
                            memoryConfig.timeLimit ||
                            60,
                        ),
                    )

                    console.log(
                        loadedOffline
                            ? '[MEMORY] Jogo da memória pronto OFFLINE.'
                            : '[MEMORY] Jogo da memória pronto ONLINE.',
                    )

                    console.log(
                        '[MEMORY] Cartas disponíveis:',
                        activeCards.length,
                    )
                } catch (error) {
                    console.error(
                        '[MEMORY] Erro ao carregar configuração:',
                        error,
                    )

                    if (
                        error?.message ===
                        'MEMORY_OFFLINE_DATA_NOT_FOUND'
                    ) {
                        message.error(
                            'Este jogo da memória ainda não foi preparado para uso offline neste dispositivo.',
                        )
                    } else {
                        message.error(
                            'Não foi possível carregar o jogo da memória.',
                        )
                    }
                } finally {
                    setLoading(false)
                }
            },
            [
                game?.id,
                game?.offlineMode,
            ],
        )

    useEffect(() => {
        loadMemoryConfig()
    }, [
        loadMemoryConfig,
    ])

    // ======================================================
    // MONTAR TABULEIRO
    // ======================================================

    const createBoard =
        useCallback(
            () => {
                if (
                    !config ||
                    !cards.length
                ) {
                    return
                }

                const pairCount =
                    Math.min(
                        Number(
                            config
                                .pairCount ||
                            4,
                        ),
                        cards.length,
                    )

                const selectedPairs =
                    cards.slice(
                        0,
                        pairCount,
                    )

                const generatedCards =
                    []

                selectedPairs.forEach(
                    (
                        card,
                    ) => {
                        const imageUrl =
                            card
                                .imageAUrl ||
                            card
                                .imageBUrl

                        generatedCards.push({
                            id:
                                `${card.id}_A`,

                            pairId:
                                card.id,

                            name:
                                card.name,

                            imageUrl,

                            matched:
                                false,
                        })

                        generatedCards.push({
                            id:
                                `${card.id}_B`,

                            pairId:
                                card.id,

                            name:
                                card.name,

                            imageUrl,

                            matched:
                                false,
                        })
                    },
                )

                setBoard(
                    shuffleArray(
                        generatedCards,
                    ),
                )

                setFirstCardId(
                    null,
                )

                setSecondCardId(
                    null,
                )

                setLocked(
                    false,
                )

                setAttempts(
                    0,
                )

                setErrors(
                    0,
                )

                setMatchedPairs(
                    0,
                )

                setCombo(
                    0,
                )

                setComboDisplay(
                    null,
                )

                setLiveScore(
                    0,
                )

                setFinished(
                    false,
                )

                setStarted(
                    false,
                )

                setAutoFlipPhase(
                    null,
                )

                setIntroPhase(
                    null,
                )

                setRevealing(
                    false,
                )

                setRevealCountdown(
                    null,
                )

                setRemainingSeconds(
                    Number(
                        config
                            .timeLimit ||
                        60,
                    ),
                )

                startedAtRef.current =
                    null

                finishedRef.current =
                    false

                autoStartRef.current =
                    false
            },
            [
                cards,
                config,
            ],
        )

    useEffect(() => {
        if (
            config &&
            cards.length
        ) {
            createBoard()
        }
    }, [
        config,
        cards,
        createBoard,
    ])

    // ======================================================
    // SALVAR PARTIDA
    // ======================================================

    const saveMatch =
        useCallback(
            async ({
                completed,
                durationMs,
                attemptsValue,
                errorsValue,
                scoreValue,
            }) => {
                if (
                    !game?.id
                ) {
                    return null
                }

                setSavingMatch(
                    true,
                )

                const startedAt =
                    startedAtRef.current
                        ? new Date(
                            startedAtRef.current,
                        ).toISOString()
                        : new Date()
                            .toISOString()

                const finishedAt =
                    new Date()
                        .toISOString()

                const payload = {
                    participant:
                        participantData,

                    completed,

                    durationMs,

                    attempts:
                        attemptsValue,

                    errors:
                        errorsValue,

                    score:
                        scoreValue,

                    startedAt,

                    finishedAt,
                }

                /*
                 * =====================================
                 * 1. TENTAR SALVAR ONLINE
                 * =====================================
                 */

                try {
                    const result =
                        await fetch({
                            url:
                                `/memory-games/${game.id}/matches`,

                            method:
                                'post',

                            silentNetworkError:
                                true,

                            data:
                                payload,
                        })

                    if (
                        result?.id
                    ) {
                        console.log(
                            '[MEMORY] Partida salva online:',
                            result.id,
                        )

                        return result
                    }

                    throw new Error(
                        'MEMORY_MATCH_INVALID_RESPONSE',
                    )
                } catch (
                apiError
                ) {
                    console.warn(
                        '[MEMORY] API indisponível. Salvando partida offline:',
                        apiError,
                    )

                    /*
                     * =====================================
                     * 2. SALVAR OFFLINE
                     * =====================================
                     */

                    try {
                        const offlineMatch =
                            await OfflineMemoryMatchService
                                .saveMatch({
                                    gameId:
                                        game.id,

                                    participant:
                                        participantData,

                                    completed,

                                    durationMs,

                                    attempts:
                                        attemptsValue,

                                    errors:
                                        errorsValue,

                                    score:
                                        scoreValue,

                                    startedAt,

                                    finishedAt,
                                })

                        console.log(
                            '[MEMORY] Partida salva offline:',
                            offlineMatch?.id,
                        )

                        return offlineMatch
                    } catch (
                    offlineError
                    ) {
                        console.error(
                            '[MEMORY] Falha ao salvar partida offline:',
                            offlineError,
                        )

                        message.error(
                            'Não foi possível salvar o resultado da partida.',
                        )

                        return null
                    }
                } finally {
                    setSavingMatch(
                        false,
                    )
                }
            },
            [
                game?.id,
                participantData,
            ],
        )

    // ======================================================
    // FINALIZAR PARTIDA
    // ======================================================

    const finishGame =
        useCallback(
            async ({
                completed,
                attemptsValue,
                errorsValue,
            }) => {
                if (
                    finishedRef
                        .current
                ) {
                    return
                }

                finishedRef.current =
                    true

                if (
                    completed
                ) {
                    playEffect(
                        config
                            ?.completeSoundUrl,
                    )
                }

                const durationMs =
                    startedAtRef
                        .current
                        ? Date.now() -
                        startedAtRef
                            .current
                        : 0

                const finalScoreMode =
                    String(
                        config
                            ?.scoreMode ||
                        'TIME_ERRORS',
                    ).toUpperCase()

                let scoreValue =
                    0

                if (
                    !completed
                ) {
                    scoreValue =
                        0
                } else if (
                    finalScoreMode ===
                    'TIME_ERRORS'
                ) {
                    scoreValue =
                        calculateScore({
                            durationMs,

                            errors:
                                errorsValue,

                            completed,
                        })
                } else if (
                    finalScoreMode ===
                    'MATCH_POINTS' ||
                    finalScoreMode ===
                    'MATCH_COMBO'
                ) {
                    scoreValue =
                        liveScore
                } else {
                    const seconds =
                        Math.floor(
                            durationMs /
                            1000,
                        )

                    scoreValue =
                        Math.max(
                            0,

                            liveScore -
                            seconds *
                            Number(
                                config
                                    ?.timePenaltyPerSec ??
                                10,
                            ),
                        )
                }

                setFinished(
                    true,
                )

                setStarted(
                    false,
                )

                const match =
                    await saveMatch({
                        completed,

                        durationMs,

                        attemptsValue,

                        errorsValue,

                        scoreValue,
                    })

                if (
                    match?.id
                ) {
                    navigate(
                        `/r/memory/${match.id}`,
                    )

                    return
                }

                if (
                    typeof onGameFinished ===
                    'function'
                ) {
                    onGameFinished({
                        completed,

                        durationMs,

                        attempts:
                            attemptsValue,

                        errors:
                            errorsValue,

                        score:
                            scoreValue,

                        match:
                            null,
                    })

                    return
                }

                finishedRef.current =
                    false

                setFinished(
                    false,
                )

                setStarted(
                    false,
                )
            },
            [
                navigate,
                onGameFinished,
                saveMatch,
                playEffect,
                config?.completeSoundUrl,
                liveScore,
                config?.scoreMode,
                config?.timePenaltyPerSec,
            ],
        )

    // ======================================================
    // TIMER
    // ======================================================

    useEffect(() => {
        if (
            !started ||
            finished
        ) {
            return undefined
        }

        const interval =
            window.setInterval(
                () => {
                    setRemainingSeconds(
                        (
                            current,
                        ) => {
                            if (
                                current <=
                                1
                            ) {
                                window.clearInterval(
                                    interval,
                                )

                                window.setTimeout(
                                    () => {
                                        finishGame({
                                            completed:
                                                false,

                                            attemptsValue:
                                                attempts,

                                            errorsValue:
                                                errors,
                                        })
                                    },
                                    0,
                                )

                                return 0
                            }

                            return (
                                current -
                                1
                            )
                        },
                    )
                },
                1000,
            )

        return () => {
            window.clearInterval(
                interval,
            )
        }
    }, [
        started,
        finished,
        attempts,
        errors,
        finishGame,
    ])

    // ======================================================
    // INICIAR AUTOMATICAMENTE
    // ======================================================

    const handleStart =
        async () => {
            if (
                !board.length ||
                savingMatch ||
                revealing ||
                introPhase ||
                autoStartRef.current
            ) {
                return
            }

            autoStartRef.current =
                true

            finishedRef.current =
                false

            setFinished(
                false,
            )

            setStarted(
                false,
            )

            setLocked(
                true,
            )

            window.dispatchEvent(
                new CustomEvent(
                    'games:idle-pause',
                ),
            )

            const introEnabled =
                config
                    ?.introEnabled !==
                false

            const shouldReveal =
                config
                    ?.revealAtStart !==
                false

            // ==================================================
            // PREPARE-SE
            // ==================================================

            if (
                introEnabled
            ) {
                setIntroPhase(
                    'prepare',
                )

                await wait(
                    Number(
                        config
                            ?.introPrepareDuration ||
                        900,
                    ),
                )
            }

            // ==================================================
            // MEMORIZAÇÃO
            // ==================================================

            if (
                shouldReveal
            ) {
                setIntroPhase(
                    'memorize',
                )

                setAutoFlipPhase(
                    'open',
                )

                setRevealing(
                    true,
                )

                const revealDuration =
                    Number(
                        config
                            ?.revealStartDuration ||
                        5000,
                    )

                const countdownEnabled =
                    config
                        ?.revealCountdownEnabled !==
                    false

                const seconds =
                    Math.max(
                        1,
                        Math.ceil(
                            revealDuration /
                            1000,
                        ),
                    )

                if (
                    countdownEnabled
                ) {
                    setRevealCountdown(
                        seconds,
                    )

                    playEffect(
                        config
                            ?.countdownSoundUrl,
                    )
                } else {
                    setRevealCountdown(
                        null,
                    )
                }

                let remaining =
                    seconds

                let countdownInterval =
                    null

                if (
                    countdownEnabled
                ) {
                    countdownInterval =
                        window.setInterval(
                            () => {
                                remaining =
                                    Math.max(
                                        0,
                                        remaining -
                                        1,
                                    )

                                setRevealCountdown(
                                    remaining,
                                )

                                if (
                                    remaining >
                                    0
                                ) {
                                    playEffect(
                                        config
                                            ?.countdownSoundUrl,
                                    )
                                }
                            },
                            1000,
                        )
                }

                await wait(
                    revealDuration,
                )

                if (
                    countdownInterval
                ) {
                    window.clearInterval(
                        countdownInterval,
                    )
                }

                setRevealCountdown(
                    null,
                )

                setAutoFlipPhase(
                    'close',
                )

                setRevealing(
                    false,
                )

                const flipDuration =
                    Number(
                        config
                            ?.flipDuration ||
                        360,
                    )

                const cascadeDelay =
                    Number(
                        config
                            ?.flipCascadeDelay ??
                        70,
                    )

                const cascadeTotal =
                    Math.max(
                        0,
                        (
                            board.length -
                            1
                        ) *
                        cascadeDelay,
                    )

                await wait(
                    cascadeTotal +
                    flipDuration +
                    120,
                )

                setAutoFlipPhase(
                    null,
                )
            }

            // ==================================================
            // VALENDO
            // ==================================================

            if (
                introEnabled
            ) {
                setIntroPhase(
                    'go',
                )

                playEffect(
                    config
                        ?.goSoundUrl,
                )

                await wait(
                    Number(
                        config
                            ?.introGoDuration ||
                        650,
                    ),
                )
            }

            // ==================================================
            // PARTIDA
            // ==================================================

            setIntroPhase(
                null,
            )

            setLocked(
                false,
            )

            startedAtRef.current =
                Date.now()

            setStarted(
                true,
            )
        }

    // ======================================================
    // AUTO START AO ENTRAR NO MEMORY
    // ======================================================

    useEffect(() => {
        if (
            loading ||
            !config ||
            !board.length ||
            autoStartRef.current
        ) {
            return
        }

        handleStart()

        // handleStart é controlado por autoStartRef.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        loading,
        config,
        board.length,
    ])

    // ======================================================
    // CLIQUE NA CARTA
    // ======================================================

    const handleCardClick =
        (
            clickedCard,
        ) => {
            if (
                !started ||
                revealing ||
                autoFlipPhase ||
                finished ||
                locked ||
                clickedCard.matched
            ) {
                return
            }

            if (
                clickedCard.id ===
                firstCardId
            ) {
                return
            }

            // ================================================
            // PRIMEIRA CARTA
            // ================================================

            if (
                !firstCardId
            ) {
                setFirstCardId(
                    clickedCard.id,
                )

                return
            }

            // ================================================
            // SEGUNDA CARTA
            // ================================================

            setSecondCardId(
                clickedCard.id,
            )

            setLocked(
                true,
            )

            const firstCard =
                board.find(
                    (
                        item,
                    ) =>
                        item.id ===
                        firstCardId,
                )

            if (
                !firstCard
            ) {
                setFirstCardId(
                    null,
                )

                setSecondCardId(
                    null,
                )

                setLocked(
                    false,
                )

                return
            }

            const nextAttempts =
                attempts +
                1

            setAttempts(
                nextAttempts,
            )

            const isMatch =
                firstCard
                    .pairId ===
                clickedCard
                    .pairId

            // ================================================
            // ACERTO
            // ================================================

            if (
                isMatch
            ) {
                playEffect(
                    config
                        ?.matchSoundUrl,
                )

                const nextMatchedPairs =
                    matchedPairs +
                    1

                const nextCombo =
                    config
                        ?.comboEnabled !==
                        false
                        ? combo +
                        1
                        : 0

                const basePoints =
                    Number(
                        config
                            ?.pointsPerMatch ??
                        100,
                    )

                let scoreMultiplier =
                    1

                if (
                    config
                        ?.comboEnabled !==
                    false &&
                    config
                        ?.comboAffectsScore !==
                    false
                ) {
                    const baseMultiplier =
                        Number(
                            config
                                ?.comboBaseMultiplier ??
                            0.5,
                        )

                    const maxMultiplier =
                        Number(
                            config
                                ?.comboMaxMultiplier ??
                            5,
                        )

                    scoreMultiplier =
                        Math.min(
                            maxMultiplier,

                            1 +
                            Math.max(
                                0,
                                nextCombo -
                                1,
                            ) *
                            baseMultiplier,
                        )
                }

                const gainedPoints =
                    Math.round(
                        basePoints *
                        scoreMultiplier,
                    )

                setLiveScore(
                    (
                        current,
                    ) =>
                        current +
                        gainedPoints,
                )

                setCombo(
                    nextCombo,
                )

                if (
                    config
                        ?.comboEnabled !==
                    false &&
                    nextCombo >=
                    2
                ) {
                    const baseMultiplier =
                        Number(
                            config
                                ?.comboBaseMultiplier ??
                            0.5,
                        )

                    const maxMultiplier =
                        Number(
                            config
                                ?.comboMaxMultiplier ??
                            5,
                        )

                    const multiplier =
                        Math.min(
                            maxMultiplier,

                            1 +
                            (
                                nextCombo -
                                1
                            ) *
                            baseMultiplier,
                        )

                    setComboDisplay({
                        combo:
                            nextCombo,

                        multiplier,
                    })

                    window.setTimeout(
                        () => {
                            setComboDisplay(
                                null,
                            )
                        },
                        Number(
                            config
                                ?.comboDisplayDuration ||
                            900,
                        ),
                    )
                }

                setBoard(
                    (
                        current,
                    ) =>
                        current.map(
                            (
                                item,
                            ) =>
                                item
                                    .pairId ===
                                    clickedCard
                                        .pairId
                                    ? {
                                        ...item,

                                        matched:
                                            true,
                                    }
                                    : item,
                        ),
                )

                setMatchedPairs(
                    nextMatchedPairs,
                )

                window.setTimeout(
                    () => {
                        setFirstCardId(
                            null,
                        )

                        setSecondCardId(
                            null,
                        )

                        setLocked(
                            false,
                        )

                        const totalPairs =
                            board.length /
                            2

                        if (
                            nextMatchedPairs >=
                            totalPairs
                        ) {
                            finishGame({
                                completed:
                                    true,

                                attemptsValue:
                                    nextAttempts,

                                errorsValue:
                                    errors,
                            })
                        }
                    },
                    300,
                )

                return
            }

            // ================================================
            // ERRO
            // ================================================

            playEffect(
                config
                    ?.errorSoundUrl,
            )

            setCombo(
                0,
            )

            setComboDisplay(
                null,
            )

            const nextErrors =
                errors +
                1

            const currentScoreMode =
                String(
                    config
                        ?.scoreMode ||
                    'TIME_ERRORS',
                ).toUpperCase()

            if (
                currentScoreMode !==
                'TIME_ERRORS'
            ) {
                const penalty =
                    Number(
                        config
                            ?.errorPenalty ??
                        50,
                    )

                setLiveScore(
                    (
                        current,
                    ) =>
                        Math.max(
                            0,
                            current -
                            penalty,
                        ),
                )
            }

            setErrors(
                nextErrors,
            )

            window.setTimeout(
                () => {
                    setFirstCardId(
                        null,
                    )

                    setSecondCardId(
                        null,
                    )

                    setLocked(
                        false,
                    )
                },
                Number(
                    config
                        ?.revealDuration ||
                    800,
                ),
            )
        }

    // ======================================================
    // CARTA ABERTA?
    // ======================================================

    const isCardOpen =
        (
            card,
        ) => {
            return (
                revealing ||
                card.matched ||
                card.id ===
                firstCardId ||
                card.id ===
                secondCardId
            )
        }

    // ======================================================
    // CONFIGURAÇÕES VISUAIS
    // ======================================================

    const configuredCardHeight =
        Number(
            config
                ?.cardSize ||
            160,
        )

    const configuredCardWidth =
        Number(
            config
                ?.cardWidth ||
            160,
        )

    const cardGap =
        Number(
            config
                ?.cardGap ||
            16,
        )

    const boardMaxWidth =
        Number(
            config
                ?.boardMaxWidth ||
            1400,
        )

    const totalCards =
        board.length

    const totalPairs =
        totalCards /
        2

    // ======================================================
    // VIEWPORT / ORIENTAÇÃO
    // ======================================================

    const viewportWidth =
        viewport.width

    const viewportHeight =
        viewport.height

    const configuredOrientation =
        String(
            config
                ?.layoutOrientation ||
            'AUTO',
        ).toUpperCase()

    const detectedPortrait =
        viewportHeight >
        viewportWidth

    const isPortrait =
        configuredOrientation ===
            'PORTRAIT'
            ? true
            : configuredOrientation ===
                'LANDSCAPE'
                ? false
                : detectedPortrait

    // ======================================================
    // GRID
    // ======================================================

    const configuredColumns =
        isPortrait
            ? Number(
                config
                    ?.portraitColumns ??
                4,
            )
            : Number(
                config
                    ?.landscapeColumns ??
                8,
            )

    const gridColumns =
        Math.max(
            1,
            Math.min(
                totalCards ||
                1,
                configuredColumns,
            ),
        )

    const gridRows =
        Math.ceil(
            totalCards /
            gridColumns,
        )

    // ======================================================
    // TAMANHO DAS CARTAS
    // USA EXATAMENTE O VALOR CONFIGURADO NO ADMIN
    // ======================================================

    const cardWidth =
        Math.max(
            40,
            Number(
                config?.cardWidth ||
                160,
            ),
        )

    const cardHeight =
        Math.max(
            60,
            Number(
                config?.cardSize ||
                160,
            ),
        )

    // ======================================================
    // TAMANHO REAL DO TABULEIRO
    // ======================================================

    const totalHorizontalGap =
        cardGap *
        Math.max(
            0,
            gridColumns - 1,
        )

    const totalVerticalGap =
        cardGap *
        Math.max(
            0,
            gridRows - 1,
        )

    const calculatedBoardWidth =
        (
            cardWidth *
            gridColumns
        ) +
        totalHorizontalGap

    const calculatedBoardHeight =
        (
            cardHeight *
            gridRows
        ) +
        totalVerticalGap

    // ======================================================
    // SCORE
    // ======================================================

    const scoreMode =
        String(
            config
                ?.scoreMode ||
            'TIME_ERRORS',
        ).toUpperCase()

    let currentScore =
        0

    if (
        scoreMode ===
        'TIME_ERRORS'
    ) {
        currentScore =
            started &&
                startedAtRef
                    .current
                ? calculateScore({
                    durationMs:
                        Date.now() -
                        startedAtRef
                            .current,

                    errors,

                    completed:
                        true,
                })
                : 10000
    } else if (
        scoreMode ===
        'MATCH_POINTS' ||
        scoreMode ===
        'MATCH_COMBO'
    ) {
        currentScore =
            liveScore
    } else {
        const elapsedSeconds =
            startedAtRef
                .current
                ? Math.floor(
                    (
                        Date.now() -
                        startedAtRef
                            .current
                    ) /
                    1000,
                )
                : 0

        const timePenalty =
            elapsedSeconds *
            Number(
                config
                    ?.timePenaltyPerSec ??
                10,
            )

        currentScore =
            Math.max(
                0,

                liveScore -
                timePenalty,
            )
    }

    // ======================================================
    // IDENTIDADE VISUAL DO GAMEPLAY
    // ======================================================

    const gameplayBackgroundColor =
        config?.gameplayBackgroundColor ||
        game?.backgroundColor ||
        '#ffffff'

    const gameplayBackgroundUrl =
        config?.gameplayBackgroundUrl ||
        game?.backgroundUrl ||
        null

    const gameplayLogoUrl =
        config?.gameplayLogoUrl ||
        game?.logoUrl ||
        null

    const gameplayPrimaryColor =
        config?.gameplayPrimaryColor ||
        game?.primaryColor ||
        '#1677ff'

    const gameplayTextColor =
        config?.gameplayTextColor ||
        game?.textColor ||
        '#111827'

    // ======================================================
    // CORES DO HUD
    // ======================================================

    const hudBackground =
        config
            ?.hudBackground ||
        'rgba(255,255,255,.94)'

    const hudTextColor =
        config
            ?.hudTextColor ||
        gameplayPrimaryColor

    const hudLabelColor =
        config
            ?.hudLabelColor ||
        hudTextColor

    // ======================================================
    // LOADING
    // ======================================================

    if (
        loading
    ) {
        return (
            <div
                style={{
                    width:
                        '100vw',

                    height:
                        '100vh',

                    display:
                        'flex',

                    alignItems:
                        'center',

                    justifyContent:
                        'center',

                    background:
                        game
                            ?.backgroundColor ||
                        '#ffffff',
                }}
            >
                <Spin
                    size="large"
                />
            </div>
        )
    }

    // ======================================================
    // SALVANDO
    // ======================================================

    if (
        finished &&
        savingMatch
    ) {
        return (
            <div
                style={{
                    width:
                        '100vw',

                    height:
                        '100vh',

                    display:
                        'flex',

                    flexDirection:
                        'column',

                    gap:
                        16,

                    alignItems:
                        'center',

                    justifyContent:
                        'center',

                    backgroundColor:
                        game
                            ?.backgroundColor ||
                        '#ffffff',

                    color:
                        game
                            ?.textColor ||
                        '#111827',
                }}
            >
                <Spin
                    size="large"
                />

                <Text
                    style={{
                        color:
                            game
                                ?.textColor ||
                            '#111827',
                    }}
                >
                    Salvando resultado...
                </Text>
            </div>
        )
    }

    // ======================================================
    // SEM CONFIGURAÇÃO
    // ======================================================

    if (
        !config
    ) {
        return (
            <div
                style={{
                    width:
                        '100vw',

                    height:
                        '100vh',

                    display:
                        'flex',

                    alignItems:
                        'center',

                    justifyContent:
                        'center',

                    padding:
                        30,

                    textAlign:
                        'center',
                }}
            >
                <div>
                    <Title
                        level={
                            2
                        }
                    >
                        Jogo da Memória
                    </Title>

                    <Text
                        type="secondary"
                    >
                        Configuração não encontrada.
                    </Text>
                </div>
            </div>
        )
    }

    // ======================================================
    // SEM CARTAS
    // ======================================================

    if (
        !board.length
    ) {
        return (
            <div
                style={{
                    width:
                        '100vw',

                    height:
                        '100vh',

                    display:
                        'flex',

                    alignItems:
                        'center',

                    justifyContent:
                        'center',

                    padding:
                        30,

                    textAlign:
                        'center',

                    backgroundColor:
                        game
                            ?.backgroundColor ||
                        '#ffffff',

                    color:
                        game
                            ?.textColor ||
                        '#111827',
                }}
            >
                Cadastre as cartas para iniciar o jogo.
            </div>
        )
    }

    // ======================================================
    // CSS DE ANIMAÇÕES
    // ======================================================

    const gameAnimations = `
        @keyframes memoryComboPop {
            0% {
                opacity: 0;
                transform:
                    translate(-50%, -50%)
                    scale(.35);
            }

            55% {
                opacity: 1;
                transform:
                    translate(-50%, -50%)
                    scale(1.18);
            }

            100% {
                opacity: 1;
                transform:
                    translate(-50%, -50%)
                    scale(1);
            }
        }

        @keyframes memoryCardEnter {
            0% {
                opacity: 0;
                transform:
                    translateY(20px)
                    scale(.9);
            }

            70% {
                opacity: 1;
                transform:
                    translateY(-2px)
                    scale(1.02);
            }

            100% {
                opacity: 1;
                transform:
                    translateY(0)
                    scale(1);
            }
        }

        @keyframes memoryIntroFade {
            0% {
                opacity: 0;
                transform:
                    translateY(-8px)
                    scale(.96);
            }

            100% {
                opacity: 1;
                transform:
                    translateY(0)
                    scale(1);
            }
        }
    `

    // ======================================================
    // JOGO
    // ======================================================

    return (
        <div
            style={{
                position:
                    'relative',

                width:
                    '100vw',

                height:
                    '100vh',

                minHeight:
                    '100vh',

                overflow:
                    'hidden',

                boxSizing:
                    'border-box',

                backgroundColor:
                    gameplayBackgroundColor,

                backgroundImage:
                    gameplayBackgroundUrl
                        ? `url(${getImageUrl(
                            gameplayBackgroundUrl,
                        )})`
                        : undefined,

                backgroundSize:
                    config
                        ?.backgroundFit ||
                    'cover',

                backgroundPosition:
                    'center',

                backgroundRepeat:
                    'no-repeat',

                color:
                    gameplayTextColor,
            }}
        >
            <style>
                {
                    gameAnimations
                }
            </style>

            {/* ================================================== */}
            {/* COMBO */}
            {/* ================================================== */}

            {comboDisplay && (
                <div
                    style={{
                        position:
                            'fixed',

                        left:
                            '50%',

                        top:
                            '50%',

                        transform:
                            'translate(-50%, -50%)',

                        zIndex:
                            100,

                        pointerEvents:
                            'none',

                        textAlign:
                            'center',

                        animation:
                            'memoryComboPop .55s cubic-bezier(.2,.9,.2,1)',
                    }}
                >
                    <div
                        style={{
                            color:
                                config
                                    ?.comboColor ||
                                '#fadb14',

                            fontSize:
                                isPortrait
                                    ? 76
                                    : 86,

                            lineHeight:
                                1,

                            fontWeight:
                                1000,

                            textShadow:
                                '0 4px 18px rgba(0,0,0,.38)',
                        }}
                    >
                        x
                        {
                            comboDisplay
                                .multiplier
                                .toFixed(
                                    1,
                                )
                        }
                    </div>

                    <div
                        style={{
                            marginTop:
                                6,

                            color:
                                game
                                    ?.textColor ||
                                '#ffffff',

                            fontSize:
                                24,

                            fontWeight:
                                900,
                        }}
                    >
                        COMBO{' '}
                        {
                            comboDisplay
                                .combo
                        }
                    </div>
                </div>
            )}

            {/* ================================================== */}
            {/* CONTAINER */}
            {/* ================================================== */}

            <div
                style={{
                    width:
                        '100%',

                    height:
                        '100%',

                    display:
                        'flex',

                    flexDirection:
                        'column',

                    alignItems:
                        'center',

                    padding:
                        isPortrait
                            ? '20px 24px 24px'
                            : '18px 36px 24px',

                    boxSizing:
                        'border-box',
                }}
            >
                {/* ============================================== */}
                {/* TOPO */}
                {/* ============================================== */}

                <div
                    style={{
                        position:
                            'relative',

                        width:
                            '100%',

                        maxWidth:
                            Math.min(
                                calculatedBoardWidth,
                                viewportWidth - 24,
                            ),

                        display:
                            'flex',

                        alignItems:
                            'center',

                        justifyContent:
                            'center',

                        minHeight:
                            isPortrait
                                ? 100
                                : 82,

                        marginBottom:
                            introPhase
                                ? 8
                                : 16,
                    }}
                >
                    {/* ========================================== */}
                    {/* LOGO */}
                    {/* ========================================== */}

                    {config
                        ?.showGameLogo !==
                        false &&
                        gameplayLogoUrl && (
                            <img
                                src={
                                    getImageUrl(
                                        gameplayLogoUrl,
                                    )
                                }
                                alt="Logo"
                                draggable={
                                    false
                                }
                                style={{
                                    position:
                                        'absolute',

                                    left:
                                        0,

                                    top:
                                        '50%',

                                    transform:
                                        'translateY(-50%)',

                                    width:
                                        Number(
                                            config
                                                ?.gameLogoWidth ||
                                            (
                                                isPortrait
                                                    ? 90
                                                    : 110
                                            ),
                                        ),

                                    maxHeight:
                                        Number(
                                            config
                                                ?.gameLogoHeight ||
                                            80,
                                        ),

                                    objectFit:
                                        'contain',

                                    userSelect:
                                        'none',
                                }}
                            />
                        )}

                    {/* ========================================== */}
                    {/* HUD */}
                    {/* ========================================== */}

                    <div
                        style={{
                            display:
                                'flex',

                            alignItems:
                                'center',

                            justifyContent:
                                'center',

                            gap:
                                isPortrait
                                    ? 26
                                    : 44,

                            padding:
                                isPortrait
                                    ? '15px 24px'
                                    : '14px 42px',

                            borderRadius:
                                Number(
                                    config
                                        ?.hudBorderRadius ||
                                    999,
                                ),

                            background:
                                hudBackground,

                            boxShadow:
                                config
                                    ?.hudShadow ===
                                    false
                                    ? 'none'
                                    : '0 12px 34px rgba(0,0,0,.12)',

                            backdropFilter:
                                'blur(12px)',

                            WebkitBackdropFilter:
                                'blur(12px)',

                            maxWidth:
                                '90%',
                        }}
                    >
                        {config
                            ?.showTimer !==
                            false && (
                                <Indicator
                                    label="Tempo"
                                    value={
                                        formatTime(
                                            remainingSeconds,
                                        )
                                    }
                                    valueColor={
                                        hudTextColor
                                    }
                                    labelColor={
                                        hudLabelColor
                                    }
                                />
                            )}

                        {config
                            ?.showAttempts !==
                            false && (
                                <Indicator
                                    label="Tentativas"
                                    value={
                                        attempts
                                    }
                                    valueColor={
                                        hudTextColor
                                    }
                                    labelColor={
                                        hudLabelColor
                                    }
                                />
                            )}

                        {config
                            ?.showScore !==
                            false && (
                                <Indicator
                                    label="Pontos"
                                    value={
                                        currentScore
                                    }
                                    valueColor={
                                        hudTextColor
                                    }
                                    labelColor={
                                        hudLabelColor
                                    }
                                />
                            )}
                    </div>
                </div>

                {/* ============================================== */}
                {/* INTRO / MEMORIZAÇÃO */}
                {/* ============================================== */}

                {introPhase && (
                    <div
                        style={{
                            width:
                                '100%',

                            maxWidth:
                                '100%',

                            minHeight:
                                isPortrait
                                    ? 105
                                    : 82,

                            display:
                                'flex',

                            flexDirection:
                                'column',

                            alignItems:
                                'center',

                            justifyContent:
                                'center',

                            textAlign:
                                'center',

                            marginBottom:
                                8,

                            pointerEvents:
                                'none',

                            flexShrink:
                                0,
                        }}
                    >
                        <div
                            key={
                                `${introPhase}-${revealCountdown}`
                            }
                            style={{
                                animation:
                                    'memoryIntroFade .28s ease',

                                color:
                                    introPhase ===
                                        'go'
                                        ? config
                                            ?.comboColor ||
                                        gameplayPrimaryColor
                                        : gameplayTextColor,
                            }}
                        >
                            <div
                                style={{
                                    fontSize:
                                        isPortrait
                                            ? 36
                                            : 28,

                                    fontWeight:
                                        900,

                                    lineHeight:
                                        1,

                                    textTransform:
                                        'uppercase',

                                    letterSpacing:
                                        1,
                                }}
                            >
                                {introPhase ===
                                    'prepare' &&
                                    (
                                        config
                                            ?.introPrepareText ||
                                        'PREPARE-SE'
                                    )}

                                {introPhase ===
                                    'memorize' &&
                                    (
                                        config
                                            ?.introMemorizeText ||
                                        'MEMORIZE AS CARTAS'
                                    )}

                                {introPhase ===
                                    'go' &&
                                    (
                                        config
                                            ?.introGoText ||
                                        'VALENDO!'
                                    )}
                            </div>

                            {introPhase ===
                                'memorize' &&
                                revealCountdown !==
                                null && (
                                    <div
                                        style={{
                                            marginTop:
                                                7,

                                            fontSize:
                                                isPortrait
                                                    ? 58
                                                    : 46,

                                            fontWeight:
                                                1000,

                                            lineHeight:
                                                1,

                                            color:
                                                gameplayPrimaryColor,
                                        }}
                                    >
                                        {
                                            revealCountdown
                                        }
                                    </div>
                                )}
                        </div>
                    </div>
                )}

                {/* ============================================== */}
                {/* TABULEIRO */}
                {/* ============================================== */}

                <div
                    style={{
                        flex:
                            1,

                        width:
                            '100%',

                        maxWidth:
                            '100%',

                        display:
                            'flex',

                        alignItems:
                            'center',

                        justifyContent:
                            'center',

                        overflow:
                            'hidden',
                    }}
                >
                    <div
                        style={{
                            display:
                                'grid',

                            gridTemplateColumns:
                                `repeat(${gridColumns}, ${cardWidth}px)`,

                            gridAutoRows:
                                `${cardHeight}px`,

                            justifyContent:
                                'center',

                            alignItems:
                                'center',

                            gap:
                                cardGap,

                            width:
                                'max-content',

                            maxWidth:
                                '100%',

                            margin:
                                '0 auto',

                            pointerEvents:
                                started &&
                                    !revealing &&
                                    !autoFlipPhase &&
                                    !introPhase
                                    ? 'auto'
                                    : 'none',
                        }}
                    >
                        {board.map(
                            (
                                card,
                                index,
                            ) => (
                                <MemoryCard
                                    key={
                                        card.id
                                    }
                                    card={
                                        card
                                    }
                                    open={
                                        isCardOpen(
                                            card,
                                        )
                                    }
                                    cardWidth={
                                        cardWidth
                                    }
                                    cardHeight={
                                        cardHeight
                                    }
                                    config={
                                        config
                                    }
                                    animationIndex={
                                        index
                                    }
                                    autoFlipPhase={
                                        autoFlipPhase
                                    }
                                    onFlip={({
                                        automatic,
                                        index:
                                        flipIndex,
                                    }) => {
                                        if (
                                            automatic
                                        ) {
                                            if (
                                                config
                                                    ?.cascadeFlipSoundEnabled ===
                                                false
                                            ) {
                                                return
                                            }

                                            const every =
                                                Math.max(
                                                    1,
                                                    Number(
                                                        config
                                                            ?.cascadeFlipSoundEvery ??
                                                        2,
                                                    ),
                                                )

                                            if (
                                                flipIndex %
                                                every !==
                                                0
                                            ) {
                                                return
                                            }
                                        }

                                        playEffect(
                                            config
                                                ?.flipSoundUrl,
                                        )
                                    }}
                                    onClick={() =>
                                        handleCardClick(
                                            card,
                                        )
                                    }
                                />
                            ),
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

// ======================================================
// CARTA
// ======================================================

const MemoryCard = ({
    card,
    open,
    cardWidth,
    cardHeight,
    config,
    animationIndex = 0,
    autoFlipPhase = null,
    onFlip,
    onClick,
}) => {
    const onFlipRef =
        useRef(
            onFlip,
        )

    useEffect(() => {
        onFlipRef.current =
            onFlip
    }, [
        onFlip,
    ])
    // ======================================================
    // FLIP
    // ======================================================

    const [
        visualOpen,
        setVisualOpen,
    ] = useState(
        open,
    )

    const [
        compressing,
        setCompressing,
    ] = useState(
        false,
    )

    const flipDuration =
        Number(
            config
                ?.flipDuration ||
            360,
        )

    const cascadeDelay =
        Number(
            config
                ?.flipCascadeDelay ??
            70,
        )

    const flipDelay =
        autoFlipPhase
            ? animationIndex *
            cascadeDelay
            : 0

    useEffect(() => {
        if (
            open ===
            visualOpen
        ) {
            return undefined
        }

        const halfDuration =
            flipDuration /
            2

        const startTimer =
            window.setTimeout(
                () => {
                    if (
                        typeof onFlipRef.current ===
                        'function'
                    ) {
                        onFlipRef.current({
                            automatic:
                                Boolean(
                                    autoFlipPhase,
                                ),

                            index:
                                animationIndex,
                        })
                    }
                    setCompressing(
                        true,
                    )
                },
                flipDelay,
            )

        const changeFaceTimer =
            window.setTimeout(
                () => {
                    setVisualOpen(
                        open,
                    )

                    setCompressing(
                        false,
                    )
                },
                flipDelay +
                halfDuration,
            )

        return () => {
            window.clearTimeout(
                startTimer,
            )

            window.clearTimeout(
                changeFaceTimer,
            )

            setCompressing(
                false,
            )
        }
    }, [
        open,
        visualOpen,
        flipDuration,
        flipDelay,
        autoFlipPhase,
        animationIndex,
    ])

    // ======================================================
    // MATCH
    // ======================================================

    const [
        matchAnimating,
        setMatchAnimating,
    ] = useState(
        false,
    )

    const [
        hiddenAfterMatch,
        setHiddenAfterMatch,
    ] = useState(
        false,
    )

    const matchEffectDuration =
        Number(
            config
                ?.matchEffectDuration ||
            650,
        )

    const matchedBehavior =
        String(
            config
                ?.matchedCardBehavior ||
            'DIM',
        ).toUpperCase()

    useEffect(() => {
        if (
            !card.matched
        ) {
            setMatchAnimating(
                false,
            )

            setHiddenAfterMatch(
                false,
            )

            return undefined
        }

        if (
            config
                ?.matchEffectEnabled ===
            false
        ) {
            if (
                matchedBehavior ===
                'HIDE'
            ) {
                setHiddenAfterMatch(
                    true,
                )
            }

            return undefined
        }

        setMatchAnimating(
            true,
        )

        const timer =
            window.setTimeout(
                () => {
                    setMatchAnimating(
                        false,
                    )

                    if (
                        matchedBehavior ===
                        'HIDE'
                    ) {
                        setHiddenAfterMatch(
                            true,
                        )
                    }
                },
                matchEffectDuration,
            )

        return () => {
            window.clearTimeout(
                timer,
            )
        }
    }, [
        card.matched,
        config?.matchEffectEnabled,
        matchedBehavior,
        matchEffectDuration,
    ])

    // ======================================================
    // ESTILO
    // ======================================================

    const borderRadius =
        Number(
            config
                ?.cardBorderRadius ??
            12,
        )

    const shadow =
        config
            ?.cardShadow !==
        false

    return (
        <button
            type="button"
            onClick={
                onClick
            }
            disabled={
                card.matched
            }
            style={{
                width:
                    cardWidth,

                height:
                    cardHeight,

                padding:
                    0,

                margin:
                    0,

                border:
                    0,

                outline:
                    0,

                borderRadius,

                overflow:
                    'visible',

                background:
                    'transparent',

                perspective:
                    '1000px',

                cursor:
                    card.matched
                        ? 'default'
                        : 'pointer',

                animation:
                    `memoryCardEnter 420ms ${animationIndex * 45}ms both cubic-bezier(.2,.8,.2,1)`,

                opacity:
                    hiddenAfterMatch
                        ? 0
                        : 1,

                visibility:
                    hiddenAfterMatch
                        ? 'hidden'
                        : 'visible',

                transform:
                    matchAnimating
                        ? 'scale(1.07)'
                        : 'scale(1)',

                filter:
                    matchAnimating
                        ? `drop-shadow(0 0 18px ${config
                            ?.matchGlowColor ||
                        '#52c41a'
                        })`
                        : 'none',

                transition:
                    `opacity ${matchEffectDuration}ms ease,
                    transform 220ms ease,
                    filter 220ms ease`,
            }}
        >
            <div
                style={{
                    position:
                        'relative',

                    width:
                        '100%',

                    height:
                        '100%',

                    overflow:
                        'hidden',

                    borderRadius,

                    transformOrigin:
                        'center center',

                    transform:
                        compressing
                            ? 'scaleX(0.02)'
                            : 'scaleX(1)',

                    transition:
                        `transform ${flipDuration / 2}ms cubic-bezier(.55,.05,.45,.95)`,

                    boxShadow:
                        shadow
                            ? '0 10px 24px rgba(0,0,0,.18)'
                            : 'none',

                    willChange:
                        'transform',
                }}
            >
                {/* ========================================== */}
                {/* VERSO */}
                {/* ========================================== */}

                {!visualOpen && (
                    <div
                        style={{
                            position:
                                'absolute',

                            inset:
                                0,

                            width:
                                '100%',

                            height:
                                '100%',

                            borderRadius,

                            overflow:
                                'hidden',

                            boxSizing:
                                'border-box',

                            background:
                                config
                                    ?.cardBackImageUrl
                                    ? `url(${getImageUrl(
                                        config
                                            .cardBackImageUrl,
                                    )}) center/cover no-repeat`
                                    : config
                                        ?.cardBackColor ||
                                    '#1677ff',
                        }}
                    >
                        {!config
                            ?.cardBackImageUrl && (
                                <div
                                    style={{
                                        width:
                                            '100%',

                                        height:
                                            '100%',

                                        display:
                                            'flex',

                                        alignItems:
                                            'center',

                                        justifyContent:
                                            'center',

                                        color:
                                            '#ffffff',

                                        fontSize:
                                            Math.max(
                                                24,
                                                Math.min(
                                                    cardWidth,
                                                    cardHeight,
                                                ) *
                                                0.25,
                                            ),

                                        fontWeight:
                                            900,
                                    }}
                                >
                                    ?
                                </div>
                            )}
                    </div>
                )}

                {/* ========================================== */}
                {/* FRENTE */}
                {/* ========================================== */}

                {visualOpen && (
                    <div
                        style={{
                            position:
                                'absolute',

                            inset:
                                0,

                            width:
                                '100%',

                            height:
                                '100%',

                            borderRadius,

                            overflow:
                                'hidden',

                            boxSizing:
                                'border-box',

                            background:
                                'transparent',

                            opacity:
                                card.matched &&
                                    !matchAnimating &&
                                    matchedBehavior ===
                                    'DIM'
                                    ? Math.max(
                                        0,
                                        Math.min(
                                            1,
                                            Number(
                                                config
                                                    ?.matchedCardOpacity ??
                                                45,
                                            ) /
                                            100,
                                        ),
                                    )
                                    : 1,
                        }}
                    >
                        <img
                            src={
                                getImageUrl(
                                    card
                                        .imageUrl,
                                )
                            }
                            alt={
                                card.name ||
                                'Carta'
                            }
                            draggable={
                                false
                            }
                            style={{
                                display:
                                    'block',

                                width:
                                    '100%',

                                height:
                                    '100%',

                                objectFit:
                                    config
                                        ?.cardImageFit ||
                                    'fill',

                                objectPosition:
                                    'center',

                                padding:
                                    0,

                                margin:
                                    0,

                                border:
                                    0,

                                borderRadius,

                                boxSizing:
                                    'border-box',

                                userSelect:
                                    'none',

                                pointerEvents:
                                    'none',
                            }}
                        />
                    </div>
                )}
            </div>
        </button>
    )
}

// ======================================================
// INDICADOR
// ======================================================

const Indicator = ({
    label,
    value,
    valueColor =
    '#0d3f9f',
    labelColor =
    '#0d3f9f',
}) => {
    return (
        <div
            style={{
                minWidth:
                    90,

                textAlign:
                    'center',
            }}
        >
            <div
                style={{
                    fontSize:
                        11,

                    fontWeight:
                        800,

                    letterSpacing:
                        0.8,

                    textTransform:
                        'uppercase',

                    color:
                        labelColor,

                    opacity:
                        0.75,
                }}
            >
                {
                    label
                }
            </div>

            <div
                style={{
                    marginTop:
                        1,

                    fontSize:
                        25,

                    fontWeight:
                        900,

                    lineHeight:
                        1.1,

                    color:
                        valueColor,
                }}
            >
                {
                    value
                }
            </div>
        </div>
    )
}

export default MemoryGame